import { getDefaultAccessors } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { CustomViewSortConfigItem } from 'src/graphql/entities/custom-view/custom-view.types';

// The attributes listed here are the only identifiers that will be stored in the database
// to track the user's selection. These attributes should remain abstract and not be
// directly tied to the database schema. Instead, they serve as logical names for the attributes,
// which can be matched to their corresponding configuration and settings in the system.
export const ProjectAttribute = {
  id: 'id',
  name: 'name',
  stage: 'stage',
  estDay1: 'estDay1',
  estSigningDate: 'estSigningDate',

  day1StartDate: 'day1StartDate',
  warrantyExpiration: 'warrantyExpiration',
  projectManager: 'projectManager',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  partnerName: 'partnerName',
  isPrimary: 'isPrimary',

  siteId: 'siteId',
  siteName: 'siteName',
  siteAddress: 'siteAddress',
  siteCity: 'siteCity',
  siteZip: 'siteZip',
  cppExpDate: 'cppExpDate',
  siteChargifySubId: 'siteChargifySubId',
  clientActivatedMembership: 'clientActivatedMembership',
  siteClientSegment: 'siteClientSegment',
  siteTOS: 'siteTOS',
  siteMembershipRegistered: 'siteMembershipRegistered',
  ownerName: 'ownerName',
  siteCreatedAt: 'siteCreatedAt',
  siteUpdatedAt: 'siteUpdatedAt',

  clientName: 'clientName',
  clientPhone: 'clientPhone',
  clientEmail: 'clientEmail',
  businessName: 'businessName',
} as const;

export type ProjectAttributeType = (typeof ProjectAttribute)[keyof typeof ProjectAttribute];

export const ProjectAttributeAccessors: Record<
  ProjectAttributeType,
  CustomViewSortConfigItem
> = {
  [ProjectAttribute.id]: getDefaultAccessors('id'),
  [ProjectAttribute.isPrimary]: {
    filter: 'address/primaryProjectId',
    display: 'isPrimaryForAddress',
  },
  [ProjectAttribute.name]: getDefaultAccessors('name'),
  [ProjectAttribute.partnerName]: getDefaultAccessors('address.partner.businessName'),
  [ProjectAttribute.stage]: {
    sort: 'projectStage/name',
    filter: 'projectStage/id',
    display: 'projectStage.name',
  },
  [ProjectAttribute.estSigningDate]: getDefaultAccessors('signingDate'),
  [ProjectAttribute.estDay1]: getDefaultAccessors('estimatedDay1Date'),
  [ProjectAttribute.day1StartDate]: getDefaultAccessors('day1StartDate'),
  [ProjectAttribute.warrantyExpiration]: getDefaultAccessors('warrantyExpirationDate'),
  [ProjectAttribute.projectManager]: {
    sort: 'platformUser/firstName',
    filter: 'ownerId',
    display: 'projectManagerFullName',
  },
  [ProjectAttribute.createdAt]: getDefaultAccessors('createdAt'),
  [ProjectAttribute.updatedAt]: getDefaultAccessors('updatedAt'),
  [ProjectAttribute.siteId]: getDefaultAccessors('address.id'),
  [ProjectAttribute.siteName]: {
    sort: ['address/city', 'address/address1', 'address/address2'],
    filter: ProjectAttribute.siteName,
    display: 'address.fullAddress',
  },
  [ProjectAttribute.siteAddress]: {
    sort: 'address/address1',
    filter: ProjectAttribute.siteAddress,
    display: 'address.address',
  },
  [ProjectAttribute.siteCity]: getDefaultAccessors('address.city'),
  [ProjectAttribute.siteZip]: getDefaultAccessors('address.zip'),
  [ProjectAttribute.cppExpDate]: getDefaultAccessors('address.cppExpDate'),
  [ProjectAttribute.siteChargifySubId]: getDefaultAccessors('address.chargifySubId'),
  [ProjectAttribute.clientActivatedMembership]: {
    sort: 'address/platformSlaId',
    filter: 'address/platformSlaId',
    display: 'address.platformSla.displayName',
  },
  [ProjectAttribute.siteTOS]: getDefaultAccessors('address.tosAccepted'),
  [ProjectAttribute.siteMembershipRegistered]: getDefaultAccessors(
    'address.creditCardProvided',
  ),
  [ProjectAttribute.siteClientSegment]: {
    sort: 'address/brand/name',
    filter: 'address/brand/id',
    display: 'address.brand.name',
  },
  [ProjectAttribute.ownerName]: {
    sort: ['address/platformUser/firstName', 'address/platformUser/lastName'],
    filter: ProjectAttribute.ownerName,
    display: 'address.ownerFullName',
  },
  [ProjectAttribute.siteCreatedAt]: getDefaultAccessors('address.createdAt'),
  [ProjectAttribute.siteUpdatedAt]: getDefaultAccessors('address.updatedAt'),
  [ProjectAttribute.clientName]: {
    sort: 'address/primaryClient/firstName',
    filter: ProjectAttribute.clientName,
    display: 'address.primaryClient.fullName',
  },
  [ProjectAttribute.clientPhone]: {
    sort: 'address/primaryClient/primaryPhoneNumber/value',
    filter: ProjectAttribute.clientPhone,
    display: 'address.primaryClient.primaryPhoneNumber',
  },
  [ProjectAttribute.clientEmail]: {
    sort: 'address/primaryClient/primaryEmail/value',
    filter: 'address/primaryClient/primaryEmail/value',
    display: 'address.primaryClient.primaryEmail',
  },
  [ProjectAttribute.businessName]: getDefaultAccessors('address.businessName'),
};
